import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

// =========================== EMAIL QUEUE ===========================
function EmailQueueTab() {
  const confirm = useConfirm();
  const [stats, setStats] = useState({ counts: { pending: 0, sent: 0, failed: 0 }, recent: [] });
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, trendRes] = await Promise.all([
        teacherApi.get("/admin/email-queue/stats"),
        teacherApi.get("/admin/email-queue/trend?days=7"),
      ]);
      setStats(statsRes.data);
      // Shorten date labels to MM-DD for compact display
      const formatted = (trendRes.data.trend || []).map((d) => ({
        ...d,
        label: d.date.slice(5),
      }));
      setTrend(formatted);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd ładowania kolejki");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const retryOne = async (id) => {
    try {
      await teacherApi.post(`/admin/email-queue/${id}/retry`);
      toast.success("Zaplanowano natychmiastową ponowną próbę");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd ponawiania");
    }
  };

  const deleteOne = async (id) => {
    const ok = await confirm({
      title: "Usunąć wiadomość z kolejki?",
      description: "Ta wiadomość zostanie trwale usunięta z kolejki SMTP. Nie zostanie ponowiona ani wysłana.",
      confirmText: "Usuń wiadomość",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await teacherApi.delete(`/admin/email-queue/${id}`);
      toast.success("Wiadomość usunięta");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd usuwania");
    }
  };

  const bulkDelete = async () => {
    const hours = window.prompt(
      "Usuń wiadomości pending starsze niż ile godzin?\n"
      + "(domyślnie 2h — OTP wygasają po 10 min, więc starsze są obsolete)",
      "2",
    );
    if (hours === null) return;
    const n = parseInt(hours, 10);
    if (Number.isNaN(n) || n < 1) {
      toast.error("Podaj liczbę całkowitą >= 1");
      return;
    }
    const ok = await confirm({
      title: "Usunąć stare wiadomości pending?",
      description: `Wszystkie wiadomości oczekujące starsze niż ${n}h zostaną trwale usunięte z kolejki. Operacja jest nieodwracalna.`,
      confirmText: `Usuń pending > ${n}h`,
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await teacherApi.delete(
        `/admin/email-queue/bulk?older_than_hours=${n}&status=pending`,
      );
      toast.success(`Usunięto ${data.deleted} wiadomości`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd bulk delete");
    }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: "#FEF08A", label: "PENDING" },
      sent: { bg: "#BBF7D0", label: "SENT" },
      failed: { bg: "#FECACA", label: "FAILED" },
    };
    const s = map[status] || { bg: "#E2E8F0", label: status?.toUpperCase() };
    return (
      <span
        className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-[10px] font-black tracking-wider"
        style={{ background: s.bg }}
        data-testid={`email-queue-status-${status}`}
      >
        {s.label}
      </span>
    );
  };

  return (
    <Section icon={Mail} title="Kolejka emaili (SMTP retry)">
      <p className="text-sm text-[#64748B] mb-4">
        Wiadomości oczekujące w kolejce z powodu transient SMTP błędów (dzienny limit 500, rate limit, timeout sieciowy).
        Worker ponawia próby co 15 min z exponential backoff (15min → 1h → 4h → 12h → 24h, max 5 prób).
      </p>

      {/* ---------- Health banner ---------- */}
      {stats.unhealthy && (
        <div
          className="brut border-2 border-[#0F172A] rounded-md p-4 mb-4 flex items-start gap-3"
          style={{ background: "#FECACA" }}
          data-testid="email-queue-unhealthy-banner"
        >
          <AlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <div className="font-black text-base mb-1">Kolejka wymaga uwagi</div>
            <div className="text-sm">
              {stats.counts.failed > 0 && (
                <>Trwale nieudane: <b>{stats.counts.failed}</b>. </>
              )}
              {stats.counts.pending >= (stats.pending_threshold || 10) && (
                <>Oczekujących: <b>{stats.counts.pending}</b> (próg: {stats.pending_threshold || 10}). </>
              )}
              Sprawdź provider SMTP (dzienny limit, dane logowania). Alert email do admina
              wysyłany jest raz na 12h, ale ten baner widać zawsze.
            </div>
          </div>
        </div>
      )}

      {/* ---------- Counts ---------- */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { key: "pending", label: "Oczekujące", bg: "#FEF08A" },
          { key: "sent", label: "Wysłane", bg: "#BBF7D0" },
          { key: "failed", label: "Nieudane", bg: "#FECACA" },
        ].map((card) => (
          <div
            key={card.key}
            className="brut border-2 border-[#0F172A] rounded-md p-4"
            style={{ background: card.bg }}
            data-testid={`email-queue-count-${card.key}`}
          >
            <div className="text-3xl font-black">{stats.counts[card.key] ?? 0}</div>
            <div className="text-xs font-bold uppercase tracking-wider">{card.label}</div>
          </div>
        ))}
      </div>

      {/* ---------- Trend chart (last 7 days) ---------- */}
      <div
        className="brut border-2 border-[#0F172A] rounded-md bg-white p-4 mb-6"
        data-testid="email-queue-trend-chart"
      >
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-5 h-5" strokeWidth={2.5} />
          <h3 className="font-black">Trend wysyłki — ostatnie 7 dni</h3>
        </div>
        {trend.length === 0 ? (
          <div className="text-sm text-[#64748B] italic">Brak danych do wyświetlenia.</div>
        ) : (
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "monospace" }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    border: "2px solid #0F172A",
                    borderRadius: 6,
                    fontFamily: "monospace",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, fontWeight: 700 }} />
                <Bar dataKey="sent" stackId="a" fill="#22C55E" name="Wysłane" />
                <Bar dataKey="pending" stackId="a" fill="#EAB308" name="Oczekujące" />
                <Bar dataKey="failed" stackId="a" fill="#EF4444" name="Nieudane" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-xl font-black">Ostatnie 20 wiadomości</h3>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={bulkDelete}
            disabled={loading || stats.counts.pending === 0}
            className="brut brut-hover bg-[#FECACA] border-2 border-[#0F172A] font-bold rounded-md"
            data-testid="email-queue-bulk-delete-btn"
            title="Usuń stare pending (np. obsolete OTP po incydencie SMTP)"
          >
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Usuń stare pending
          </Button>
          <Button
            size="sm"
            onClick={load}
            disabled={loading}
            className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
            data-testid="email-queue-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} strokeWidth={2.5} />
            Odśwież
          </Button>
        </div>
      </div>

      {/* ---------- Rows ---------- */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="email-queue-list">
        {stats.recent.length === 0 && (
          <div className="text-sm text-[#64748B] italic">Kolejka pusta — wszystkie emaile wysłane od ręki.</div>
        )}
        {stats.recent.map((r) => (
          <div
            key={r.id}
            className="brut border-2 border-[#0F172A] rounded-md p-3 bg-white text-xs font-mono"
            data-testid={`email-queue-row-${r.id}`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {statusBadge(r.status)}
              <span className="font-black break-all">{r.to}</span>
              <span className="text-[#64748B] ml-auto">{new Date(r.queued_at).toLocaleString("pl")}</span>
            </div>
            <div className="font-bold text-[13px] break-all">{r.subject}</div>
            <div className="text-[#475569] mt-1">
              Próby: <b>{r.attempt_count}</b> · Następna: {r.next_attempt_at ? new Date(r.next_attempt_at).toLocaleString("pl") : "—"}
              {r.sent_at && <> · Wysłano: {new Date(r.sent_at).toLocaleString("pl")}</>}
            </div>
            {r.last_error && (
              <div className="text-[#B91C1C] mt-1 break-all">⚠ {r.last_error}</div>
            )}
            {(r.status === "pending" || r.status === "failed") && (
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={() => retryOne(r.id)}
                  className="brut brut-hover bg-[#FEF08A] font-bold rounded-md text-xs h-7"
                  data-testid={`email-queue-retry-${r.id}`}
                >
                  Ponów teraz
                </Button>
                <Button
                  size="sm"
                  onClick={() => deleteOne(r.id)}
                  className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-xs h-7"
                  data-testid={`email-queue-delete-${r.id}`}
                >
                  <Trash2 className="w-3 h-3 mr-1" strokeWidth={2.5} />
                  Usuń
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}



export default EmailQueueTab;
